import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { LogCategory, LogType, LogWidget, LoggerService } from 'src/app/core/services/logger.service';
import { TransferCreditsService, validate } from 'src/app/services/transfer-credits.service';
import { AuthenticationService } from '../../../core/services/authentication.service';

//  COMPONENT IS OBSOLETE AND CAN BE REMOVED ONCE THERE ARE NO ACTIVE OLD REQUEST OBJECTS
@Component({
    selector: 'app-credit-request',
    templateUrl: './new-request.component.html',
    styleUrls: ['./new-request.component.scss'],
    standalone: false
})
export class NewRequestComponent implements OnInit, OnDestroy {
    @Input() request: CreditRequest;

    public approvedClasses$: Observable<any>;
    public syllabusFile$: Observable<string>;
    public requestId$: Observable<number>;
    public lookups$: Observable<any>;

    public isNewRequest: boolean = false;
    public uploadError: boolean[] = [false];
    public uploadSuccess: boolean[] = [false];
    public uploadFileSizeError: boolean[] = [false];
    public isNextLoading: boolean = false;
    public isPrevLoading: boolean = false;
    public isSubmitting: boolean = false;
    public hasSubmitted: boolean[] = [false];
    public hasSubmitError: boolean = false;
    public maxNoJustificationChars: number = 1000;
    public discontinuedEmphasis: string[] = [];
    public isCurrentEmphasis: boolean = true;
        
    public stageNumber$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public stages: any[] = [
        { Name: 'Location', isActive: false, isComplete: false , show: true},
        { Name: 'Class', isActive: false, isComplete: false , show: true},
        { Name: 'Credit', isActive: false, isComplete: false , show: true},
        { Name: 'Emphasis', isActive: false, isComplete: false , show: false},
        { Name: 'Submit', isActive: false, isComplete: false, show: true }
    ];

    public stages$: Observable<any> = this.stageNumber$.pipe(
        startWith(0),
        switchMap((newValue) => {
            //set all active and complete to false
            this.stages.map(s => {
                s.isActive = false;
                s.isComplete = false;
            })
            this.stages[newValue].isActive = true;
            for (var i = 0; i < newValue; i++) {
                this.stages[i].isComplete = true;
            }           
            return of(this.stages);
        })
    )
      

    constructor(
        private logger: LoggerService,
        public transferCredits: TransferCreditsService,
        private auth: AuthenticationService
    ) {

        this.lookups$ = this.transferCredits.lookups$.pipe(
            tap((lookups) => {
                const emphasis = lookups.emphasisOptionsList;

                this.discontinuedEmphasis = emphasis.filter(e => e.Key.includes("(discontinued)"))
                    .map(e => e.Value);

                if (!!this.request.AreaOfEmphasis && !emphasis.some(e => e.Value == this.request.AreaOfEmphasis))
                    this.isCurrentEmphasis = false;
            }),
            shareReplay(1),
        )
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.TransferCredits, category);
    }
       
    ngOnDestroy(): void {          
        this.log("ngOnDestroy", "", LogCategory.Destroy);
    }

    ngOnInit(): void {        
        this.log("ngOnInit", null, LogCategory.Init);        
        this.uploadError = [false];
        this.uploadSuccess = [false];
        this.transferCredits.hasSaveError = false;
        if (this.request == null) {
            this.request = new CreditRequest();
            this.request.HasEmphasis = false;
            this.isNewRequest = true;
            this.request.validate();
        }
        else {
            if (!!this.request.HostUniversity)
                this.transferCredits.university$.next(this.request.HostUniversity); //preload the list of approved classes
            if (!!this.request.UncCourse)
                this.stages[3].show = this.setEmphasisShow(this.request);          
        }
    }

   
    public clearCurrentRequest(): void {
        this.request = new CreditRequest();       
        this.request.HasEmphasis = false;
        this.stages[3].show = false;
        this.stageNumber$.next(0);
        this.approvedClasses$ = of({options:{}, dto:{}});
        this.transferCredits.hasSaveError = false;
        this.transferCredits.saveStatus$.next('') 
    }
   
    public changeApprovedClass(lookupId, lookups) {
        var approvedRequest = lookups[lookupId];        
        if (approvedRequest != null) {
            this.request.HostCourseName = approvedRequest.HostCourseName;
            this.request.HostCourseNumber = approvedRequest.HostCourseNumber;
            this.request.HostCreditHours = approvedRequest.HostCreditHours;
            this.request.UncCourse = approvedRequest.UncCourse;
            if (this.request.UncCourse == 'other')
                this.request.otherUncCourse = approvedRequest.otherUncCourse;
            this.request.UncCourseHours = approvedRequest.UncCourseHours;
            this.request.AreaOfEmphasis = approvedRequest.AreaOfEmphasis;
            this.request.UncEquivalentToSubstitute = approvedRequest.UncEquivalentToSubstitute;
            if (this.request.AreaOfEmphasis != '')
                this.request.HasEmphasis = true;
        }
    }
        
    public uploadFile(event) {
        this.uploadFileSizeError[this.request.Id] = false;
        if (event.target.files.length > 0) {
            // Limited to files of 4Mb only, to increase this you also need to update the web.config. See this article: https://stackoverflow.com/questions/3853767/maximum-request-length-exceeded
            if (event.target.files[0].size > 4194304) {
                this.uploadFileSizeError[this.request.Id] = true;
            } else {               
                this.transferCredits.updateSyllabus(event.target.files[0], this.request.Id).subscribe( //subscribe in component so we can set the syllabus filename.
                    (updatedFile) => {
                        this.log("updateSyllabus", `updated syllabus`, LogCategory.SubscriptionData);
                        this.request.SyllabusFilename = updatedFile.SyllabusFilename;
                        this.request.SyllabusFileType = updatedFile.SyllabusFileType;
                        this.uploadSuccess[this.request.Id] = true;
                    },
                    error => {
                        this.log("updateSyllabus", error, LogCategory.SubscriptionData);
                        this.uploadError[this.request.Id] = true;
                    });         
            }             
        }
    }

    public submit(request) {        
        request.validate();        
        if (request.isValid) {
            if (!request.HasEmphasis) {
                request.AreaOfEmphasis = '';
                request.UncEquivalentToSubstitute = '';
                request.AnticipatedGraduation = '';
                request.GradMonth = '';
                request.GradYear = 0;
                request.AoeJustification = '';
            } else {
                this.setGradMonthAndYear(request);
            }

            if (request.UncCourse != 'General Education') {
                request.GeRequirement = '';
                request.HasFinalExam = false;
                request.HasWrittenWork = false;
            }
            request.Disclaimer = true;
            request.isSubmitted = true;
            this.isSubmitting = true;
            this.transferCredits.saveRequest(request).subscribe(
                (savedRequest: any) => {
                    this.log("submitRequest", "submitted request", LogCategory.SubscriptionData);
                    this.transferCredits.refreshSubject$.next(true);
                    this.transferCredits.saveStatus$.next('') 
                    this.isSubmitting = false;
                    this.hasSubmitted[request.Id] = true;
                    this.hasSubmitError = false;
                },
                error => {
                    this.log("submitRequestError", error, LogCategory.SubscriptionData);
                    this.transferCredits.refreshSubject$.next(true);
                    this.transferCredits.saveStatus$.next('')
                    this.isSubmitting = false;
                    this.hasSubmitted[request.Id] = false;
                    this.hasSubmitError = true;
                },
                () => {
                }
            )
        }
    }
    
    private _status: number = -1; //status - not started (-1), pending(0), waiting(2)
    private _stageNumber: number = 0;
    private _index: number = -1;
    public save(request: CreditRequest, stageNumber: number, index: number) {
        //  IMPORTANT!!  Setting this for the form so we can have this process correctly.
        request.version = 'v2020';

        this.setEmphasisTab(request);
        this.setGradMonthAndYear(request);
        if (stageNumber > 0) {
            this.isNextLoading = true;
            if ((this.stageNumber$.value == 2) && (!this.stages[3].show))
                stageNumber += 1;
        }
        else if (stageNumber < 0) {
            this.isPrevLoading = true;
            if ((this.stageNumber$.value == 4) && (!this.stages[3].show))
                stageNumber -= 1;
        }
        request.validate();

        if ((request.Id == 0) && (this._status == 0 || this._status == 2) && (this.transferCredits.hasSaveError != true ) ) {
            //we need to wait until it has an id set
            this._status = 2;
            this._stageNumber = stageNumber;
            this._index = index;
        } else {
            this._status = 0;
            if (!!!request.Id)
                request.Id = 0;
            this.saveRequest(request, stageNumber, index);
        }
       
    }

    private saveRequest(request: CreditRequest, stageNumber: number, index: number) {
        //  Adding this property for compatibility with new PAC request object
        request['RequestId'] = request.Id;
        this.transferCredits.saveRequest(request)
            .pipe(
                catchError(err => {
                    this.log("saveRequestError", err, LogCategory.SubscriptionData);
                    let errorMessage = 'Updates not saved.'
                    if (err.status === 401)
                        errorMessage = 'You do not have permission to add a request.';
                    request.filename = '';
                    this.setNavigation(stageNumber, index);
                    this.transferCredits.hasSaveError = true;
                    this.transferCredits.saveStatus$.next(errorMessage);
                    return throwError(err);
                })
            )
            .subscribe(
                (savedRequest: any) => {
                    this.log("saveRequest", `updated request`, LogCategory.SubscriptionData);                    
                    request.Id = savedRequest.Request.Id;
                    request.filename = '';
                    this.setNavigation(stageNumber, index);
                    this.transferCredits.refreshSubject$.next(true);
                    this.transferCredits.hasSaveError = false;                    
                    this.transferCredits.saveStatus$.next('Saved!');
                    if (this._status == 2) {//waiting                    
                        this._status = 0; //pending                       
                        this.save(request, this._stageNumber, this._index); 
                    }                    
                },
                error => {
                    this.log("saveRequestError", error, LogCategory.SubscriptionData);
                    //setTimeout(() => { this.transferCredits.saveStatus$.next('') }, 2000);
                },
                () => {
                    setTimeout(() => { this.transferCredits.saveStatus$.next('') }, 2000)
                }
            );
    }
   
    private setNavigation(stageNumber: number, index: number) {
        if (index < 0)
            this.stageNumber$.next(this.stageNumber$.value + stageNumber);
        else
            this.stageNumber$.next(index);
        this.isNextLoading = false;
        this.isPrevLoading = false;
    }

    public newRequest(id) {
        this.hasSubmitted[id] = false;
        this.request = new CreditRequest();
        this.request.HasEmphasis = false;
        this.stages[3].show = false;
        this.transferCredits.hasSaveError = false;
        this.stageNumber$.next(0);
    }

    public setGradMonthAndYear(request) {

        if ((request.HasEmphasis) && (typeof request.AnticipatedGraduation !== 'undefined') && ((request.AnticipatedGraduation != '') || (!!request.AnticipatedGraduation))) {
            var monthYear = request.AnticipatedGraduation.split(' ');
            if (monthYear.length == 2) {
                request.GradMonth = monthYear[0];
                request.GradYear = monthYear[1];
            }
        } 
    }

    public setEmphasisTab(request) {
        this.stages[3].show = this.setEmphasisShow(request);
    }

    public setEmphasisShow(request) {
        var show = false;
        switch (request.UncCourse) {
            case "Business Elective":
            case "First Minor":
            case "other":
                show = true;
                break;
            default:
                request.HasEmphasis = false;
                break;
        }
        return show;
    }

    public clearEmphasisFields(request) {
        request.AreaOfEmphasis = '';
        request.UncEquivalentToSubstitute = '';
        request.AnticipatedGraduation = '';
        request.GradMonth = '';
        request.GradYear = 0;
        request.AoeJustification = '';
    }

}

export class CreditRequest {
    
    HostCreditHours: number;
    UncCourseHours: number;
    Created: string;

    Id: number;
    HostUniversity: string;
    Term: string;        
    otherHostUniversity: string;
    HostCourseName: string;
    HostCourseNumber: string;
    UncCourse: string;
    SyllabusFilename: string;
    SyllabusFileType: string;
    approvedClass: string;
    otherUncCourse: string;
    isValid: boolean;
    errors: any;
    filename: string;
    HasEmphasis: boolean;
    AreaOfEmphasis: string; 
    AnticipatedGraduation: string;
    GradMonth: string;
    GradYear: number;
    AoeJustification: string;
    UncEquivalentToSubstitute: string;

    version: string;

    constructor() {       
    }

    validate(): void {
        validate.apply(this); // allows us to set 'this' in the function
    }
    
}
